import type { Dispatch } from 'react';
import { useReducer } from 'react';

import { scrollToTop } from '@swf/core/utils';

import type { LimitResponse } from '../ResponsibleGaming/shared/definitions';

export enum DispatchActionType {
  Reset = 'reset',
  Error = 'error',
  Loading = 'loading',
  Response = 'response',
  ShowConfirm = 'showConfirm',
  SameLimits = 'sameLimits',
  IncreaseNotAllowed = 'increaseNotAllowed',
  InvalidPassword = 'invalidPassword',
  InvalidZipPassword = 'invalidZipPassword',
  InvalidSocialSecurityNumber = 'invalidSocialSecurityNumber',
  InvalidSelfExclusionValue = 'invalidSelfExclusionValue',
  BlockedLimitsPartiallyIncreased = 'blockedLimitsPartiallyIncreased',
  ToggleShowTermsAndConditions = 'toggleShowTermsAndConditions'
}

interface State<T = LimitResponse> {
  error: boolean;
  loading: boolean;
  sameLimits: boolean;
  showConfirm: boolean;
  increaseNotAllowed: boolean;
  invalidPassword: boolean;
  invalidZipPassword: boolean;
  invalidSocialSecurityNumber: boolean;
  invalidSelfExclusionValue: boolean;
  blockedLimitsPartiallyIncreased: boolean;
  response?: T;
  showTermsAndConditions: boolean;
}

export type Action<T = LimitResponse> =
  | { type: DispatchActionType.Reset }
  | { type: DispatchActionType.Error }
  | { type: DispatchActionType.Loading }
  | { type: DispatchActionType.ShowConfirm }
  | { type: DispatchActionType.SameLimits }
  | { type: DispatchActionType.IncreaseNotAllowed }
  | { type: DispatchActionType.InvalidPassword }
  | { type: DispatchActionType.InvalidZipPassword }
  | { type: DispatchActionType.InvalidSocialSecurityNumber }
  | { type: DispatchActionType.InvalidSelfExclusionValue }
  | { type: DispatchActionType.BlockedLimitsPartiallyIncreased }
  | { type: DispatchActionType.Response; payload?: T }
  | { type: DispatchActionType.ToggleShowTermsAndConditions };

const initialState = {
  error: false,
  loading: false,
  showConfirm: false,
  sameLimits: false,
  increaseNotAllowed: false,
  invalidPassword: false,
  invalidZipPassword: false,
  invalidSocialSecurityNumber: false,
  invalidSelfExclusionValue: false,
  blockedLimitsPartiallyIncreased: false,
  showTermsAndConditions: false
} as const satisfies State<undefined>;

const reducer = <T>(state: State<T>, action: Action<T>): State<T> => {
  switch (action.type) {
    case DispatchActionType.Reset:
      return initialState;
    case DispatchActionType.Error:
      return { ...initialState, error: true };
    case DispatchActionType.Loading:
      return { ...initialState, loading: true };
    case DispatchActionType.ShowConfirm:
      return { ...initialState, showConfirm: true };
    case DispatchActionType.SameLimits:
      return { ...initialState, sameLimits: true };
    case DispatchActionType.Response:
      return { ...initialState, response: action.payload };
    case DispatchActionType.IncreaseNotAllowed:
      return { ...initialState, increaseNotAllowed: true };
    case DispatchActionType.InvalidPassword:
      return { ...initialState, invalidPassword: true, showConfirm: true };
    case DispatchActionType.InvalidZipPassword:
      return { ...initialState, invalidZipPassword: true };
    case DispatchActionType.InvalidSocialSecurityNumber:
      return { ...initialState, invalidSocialSecurityNumber: true, showConfirm: true };
    case DispatchActionType.InvalidSelfExclusionValue:
      return { ...initialState, invalidSelfExclusionValue: true };
    case DispatchActionType.BlockedLimitsPartiallyIncreased:
      return { ...initialState, blockedLimitsPartiallyIncreased: true };
    case DispatchActionType.ToggleShowTermsAndConditions:
      return { ...initialState, showTermsAndConditions: !state.showTermsAndConditions };
    default:
      return state;
  }
};

const useStateReducer = <T = LimitResponse>(
  useScrollTop = true
): [State<T>, Dispatch<Action<T>>] => {
  const [state, dispatchAction] = useReducer(reducer, initialState);
  useScrollTop && scrollToTop();

  return [state as State<T>, dispatchAction];
};

export default useStateReducer;
